import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import API from 'app/api';
import { setSnackbar } from './snackbarSlice';
import { getSiteList, deleteSite, runInBatches, bulkSnackbar } from './siteSlice';

export const createOrg = createAsyncThunk(
  'org/create',
  async (orgData, { dispatch, rejectWithValue }) => {
    try {
      const response = await API.post('/CreateOrg', orgData);
      dispatch(setSnackbar({ status: 'success', message: 'Organization created successfully' }));
      return response.data;
    } catch (error) {
      dispatch(setSnackbar({ status: 'error', message: `Received status code ${error.message}` }));
      return rejectWithValue(error.message);
    }
  }
);

export const getAllOrgs = createAsyncThunk(
  'org/getAll',
  async (_, { rejectWithValue }) => {
    try {
      const response = await API.get('/GetAllOrgs');
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateOrg = createAsyncThunk(
  'org/update',
  async ({ orgId, updatedOrgData }, { dispatch, rejectWithValue }) => {
    try {
      await API.put('/UpdateOrg', { ...updatedOrgData, id: orgId });
      dispatch(setSnackbar({ status: 'success', message: 'Organization updated successfully' }));
      dispatch(getAllOrgs());
      dispatch(getOrgById(orgId));
      return { orgId, inputData: { ...updatedOrgData, id: orgId } };
    } catch (error) {
      dispatch(setSnackbar({ status: 'error', message: `Received status code ${error.message}` }));
      return rejectWithValue(error.message);
    }
  }
);

// Admin bulk edit: sets proLicenseQty on several orgs and revokes device
// licenses above it (0 pulls every license).
export const setOrgsLicenses = createAsyncThunk(
  'org/setLicenses',
  async ({ orgIds, proLicenseQty }, { dispatch, rejectWithValue }) => {
    try {
      const { data } = await API.put('/BulkSetOrgLicenses', { orgIds, proLicenseQty });
      const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;
      dispatch(
        setSnackbar({
          status: 'success',
          message: `Updated ${plural(data.orgsUpdated, 'organization')}; revoked ${plural(data.licensesRevoked, 'device license')}`,
        })
      );
      dispatch(getAllOrgs());
      return data;
    } catch (error) {
      dispatch(setSnackbar({ status: 'error', message: `Received status code ${error.message}` }));
      return rejectWithValue(error.message);
    }
  }
);

// Admin only: flags an org as a sales prospect for the admin Overview page.
export const setOrgProspect = createAsyncThunk(
  'org/setProspect',
  async ({ orgId, isProspect }, { dispatch, rejectWithValue }) => {
    try {
      await API.put('/UpdateOrgProspect', { orgId, isProspect });
      return { orgId, isProspect };
    } catch (error) {
      dispatch(setSnackbar({ status: 'error', message: `Received status code ${error.message}` }));
      return rejectWithValue(error.message);
    }
  }
);

// Admin bulk action for the Organizations page: one UpdateOrgProspect per org.
export const bulkSetOrgsProspect = createAsyncThunk(
  'org/bulkSetProspect',
  async ({ orgIds, isProspect }, { dispatch }) => {
    const result = await runInBatches(orgIds, (orgId) =>
      API.put('/UpdateOrgProspect', { orgId, isProspect })
    );
    bulkSnackbar(dispatch, result, 'Updated', 'organization');
    return { ...result, isProspect };
  }
);

export const deleteOrg = createAsyncThunk(
  'org/delete',
  async (orgId, { dispatch, rejectWithValue }) => {
    try {
      await API.delete('/DeleteOrg', { params: { OrgId: orgId } });
      dispatch(setSnackbar({ status: 'success', message: 'Organization is now inactive' }));
      dispatch(getAllOrgs());
      return orgId;
    } catch (error) {
      dispatch(setSnackbar({ status: 'error', message: `Received status code ${error.message}` }));
      return rejectWithValue(error.message);
    }
  }
);

export const getOrgById = createAsyncThunk(
  'org/getById',
  async (orgId, { rejectWithValue }) => {
    try {
      const response = await API.get('/GetOrgByID', { params: { OrgId: orgId } });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const getAllSitesByOrgId = createAsyncThunk(
  'org/getSites',
  async (orgId, { rejectWithValue }) => {
    try {
      const response = await API.get('/GetAllSitesByOrgID', { params: { OrgId: orgId } });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const addSiteToOrg = createAsyncThunk(
  'org/addSite',
  async ({ orgId, siteId }, { dispatch, rejectWithValue }) => {
    try {
      const response = await API.post('/AddSiteToOrg', null, { params: { OrgId: orgId, SiteId: siteId } });
      dispatch(setSnackbar({ status: 'success', message: response.data.message }));
      dispatch(getSiteList());
      dispatch(getAllOrgs());
      return response.data;
    } catch (error) {
      dispatch(setSnackbar({ status: 'error', message: `Received status code ${error.message}` }));
      return rejectWithValue(error.message);
    }
  }
);

export const getAllUsersByOrgId = createAsyncThunk(
  'org/getUsers',
  async (orgId, { rejectWithValue }) => {
    try {
      const response = await API.get('/GetAllUsersByOrgID', { params: { OrgId: orgId } });
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) {
        return [];
      }
      return rejectWithValue(error.message);
    }
  }
);

export const updateOrgImage = createAsyncThunk(
  'org/updateImage',
  async ({ image, orgId }, { dispatch, rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append('file', image);
      const response = await API.post('/UploadOrgPhoto', formData, { params: { OrgId: orgId } });
      dispatch(setSnackbar({ status: 'success', message: 'Organization image updated successfully' }));
      dispatch(getOrgById(orgId));
      return response.data;
    } catch (error) {
      dispatch(setSnackbar({ status: 'error', message: `Received status code ${error.message}` }));
      return rejectWithValue(error.message);
    }
  }
);

const orgSlice = createSlice({
  name: 'orgs',
  initialState: {
    orgsList: [],
    orgSiteList: [],
    org: null,
    orgUsersList: [],
    loading: false,
    error: null,
  },
  reducers: {
    clearOrgUserList: (state) => {
      state.orgUsersList = [];
    },
    clearOrg: (state) => {
      state.org = {};
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createOrg.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createOrg.fulfilled, (state, action) => {
        state.loading = false;
        state.org = action.payload;
      })
      .addCase(createOrg.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(getAllOrgs.pending, (state) => {
        state.loading = true;
      })
      .addCase(getAllOrgs.fulfilled, (state, action) => {
        state.loading = false;
        state.orgsList = action.payload;
      })
      .addCase(getAllOrgs.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateOrg.fulfilled, (state, action) => {
        const updatedOrgs = state.orgsList.map((org) =>
          org.id === action.payload.orgId ? action.payload.inputData : org
        );
        state.orgsList = updatedOrgs;
      })
      .addCase(setOrgProspect.fulfilled, (state, action) => {
        const org = state.orgsList.find((o) => o.id === action.payload.orgId);
        if (org) org.isProspect = action.payload.isProspect;
      })
      .addCase(bulkSetOrgsProspect.fulfilled, (state, action) => {
        const { succeeded, isProspect } = action.payload;
        state.orgsList.forEach((org) => {
          if (succeeded.includes(org.id)) org.isProspect = isProspect;
        });
      })
      .addCase(deleteOrg.fulfilled, (state, action) => {
        state.orgsList = state.orgsList.filter((org) => org.id !== action.payload);
      })
      .addCase(getOrgById.fulfilled, (state, action) => {
        state.org = action.payload;
      })
      .addCase(getAllSitesByOrgId.fulfilled, (state, action) => {
        state.orgSiteList = action.payload;
      })
      .addCase(addSiteToOrg.fulfilled, (state, action) => {
        state.orgSiteList = [...state.orgSiteList, action.payload];
      })
      .addCase(deleteSite.fulfilled, (state, action) => {
        const removedId = Number(action.meta.arg?.siteId);
        state.orgSiteList = state.orgSiteList.filter((site) => site.id !== removedId);
      })
      .addCase(getAllUsersByOrgId.fulfilled, (state, action) => {
        state.orgUsersList = action.payload;
      });
  },
});

export const { clearOrgUserList, clearOrg } = orgSlice.actions;
export default orgSlice.reducer;
